/**
 * RequireRole — the single route guard implementation.
 *
 * Every admin/staff/internal route funnels through this component so the
 * "am I allowed here" logic, loading state and fallback behaviour exist once.
 * Thin wrappers (RequireAppAdmin, RequireStaff, InternalToolGuard) only choose
 * the allowed roles and the denial behaviour.
 */
import { ReactNode, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useUserRole, type UserRole } from "@/hooks/useUserRole";
import { supabase } from "@/integrations/supabase/client";
import NotFound from "@/pages/NotFound";

export interface RequireRoleProps {
  children: ReactNode;
  /** Roles allowed through. */
  allowedRoles: UserRole[];
  /**
   * How to treat a signed-in user without one of the allowed roles:
   *  - "redirect": toast + send them to `deniedRedirect`
   *  - "notfound": render the 404 page in place (internal tools stay invisible)
   */
  onDenied?: "redirect" | "notfound";
  /** Where a signed-in but unauthorised user lands. */
  deniedRedirect?: string;
  /** Message shown on denial when redirecting. */
  deniedMessage?: string;
  /**
   * Re-validate the session against the auth server before rendering, so a
   * stale client flag can never flash privileged UI.
   */
  verifySession?: boolean;
}

export function GuardStatus({ label }: { label: string }) {
  return (
    <div
      role="status"
      aria-live="polite"
      className="flex min-h-[400px] flex-col items-center justify-center gap-3"
    >
      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-hidden />
      <p className="text-sm text-muted-foreground">{label}</p>
    </div>
  );
}

export function RequireRole({
  children,
  allowedRoles,
  onDenied = "redirect",
  deniedRedirect = "/app",
  deniedMessage = "Admin access required",
  verifySession = false,
}: RequireRoleProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { role, isLoading: roleLoading } = useUserRole();
  const [sessionVerified, setSessionVerified] = useState<boolean | null>(
    verifySession ? null : true,
  );

  useEffect(() => {
    if (!verifySession) return;
    let cancelled = false;
    if (authLoading) return;
    if (!isAuthenticated) {
      setSessionVerified(false);
      return;
    }
    supabase.auth.getUser().then(({ data }) => {
      if (!cancelled) setSessionVerified(!!data.user);
    });
    return () => {
      cancelled = true;
    };
  }, [verifySession, authLoading, isAuthenticated]);

  const checking = authLoading || roleLoading || (verifySession && sessionVerified === null);
  const signedIn = verifySession ? !!sessionVerified : isAuthenticated;
  const allowed = signedIn && role !== null && allowedRoles.includes(role);

  useEffect(() => {
    if (checking || onDenied === "notfound") return;

    if (!signedIn) {
      const redirect = encodeURIComponent(location.pathname + location.search);
      navigate(`/login?redirect=${redirect}`, { replace: true });
      return;
    }

    if (!allowed) {
      toast.error(deniedMessage);
      navigate(deniedRedirect, { replace: true });
    }
  }, [
    checking,
    signedIn,
    allowed,
    onDenied,
    deniedMessage,
    deniedRedirect,
    location.pathname,
    location.search,
    navigate,
  ]);

  if (checking) return <GuardStatus label="Verifying access…" />;
  if (!allowed) {
    if (onDenied === "notfound") return <NotFound />;
    return signedIn ? null : <GuardStatus label="Redirecting to sign in…" />;
  }

  return <>{children}</>;
}

export default RequireRole;
